import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Icon, Select } from '@nazka/nazka.mapframe.components';

import { useTranslation } from '@utils/i18n/i18n.consumers';
import { REGIONS, SCENARIOS } from '@App/pages/RiskDataPage/risk-data-page.constants';
import { RISK_DATA_REGIONS } from '@App/pages/RiskDataPage/risk-data';
import { trackEvent } from '@services/analytics.service';
import TranslatedSelect from '@App/components/TranslatedSelect/TranslatedSelect';

import RiskDataOverview from './RiskDataOverview/RiskDataOverview';
import RiskDataTabLayout from './RiskDataTabLayout/RiskDataTabLayout';
import RiskDataFire from './RiskDataFire/RiskDataFire';
import RiskDataHeatWave from './RiskDataHeatWave/RiskDataHeatWave';
import RiskDataDrySpell from './RiskDataDrySpell/RiskDataDrySpell';
import RiskDataExtremePrecipitation from './RiskDataExtremePrecipitation/RiskDataExtremePrecipitation';
import RiskDataExtremeWind from './RiskDataExtremeWind/RiskDataExtremeWind';

import { TABS } from './risk-data-content.constants';
import { createRiskMarkers } from './risk-data-content.utils';

import './risk-data-content.styl';

function RiskDataContent({
  map,
  onSelectedScenarioChange,
  riskData,
  selectedHorizon,
  selectedRegion,
  selectedRisk,
  selectedScenario,
  setSelectedRegion,
  setSelectedRisk,
}) {
  const [selectedTab, setSelectedTab] = useState('overview');
  const [openRisk, setOpenRisk] = useState(null);

  const { t } = useTranslation();

  const riskMarkersRef = useRef();

  const isRiskEnabled = useCallback(riskId => selectedRisk === riskId, [selectedRisk]);

  const handleRiskEnabledChange = useCallback((riskId, nextEnabled) => {
    if (nextEnabled) {
      setSelectedRisk(riskId);
      return;
    }

    if (selectedRisk === riskId) {
      const defaultRisk = TABS.find(tab => tab.id === selectedTab)?.defaultRisk;
      setSelectedRisk(defaultRisk || null);
    }
  }, [selectedRisk, selectedTab, setSelectedRisk]);

  useEffect(() => {
    const shouldHaveMarkers = map
      && selectedRisk
      && !['extreme-precipitation', 'extreme-wind'].includes(selectedRisk);

    if (shouldHaveMarkers) {
      riskMarkersRef.current = createRiskMarkers(map, RISK_DATA_REGIONS, selectedRisk, selectedHorizon, selectedScenario);
    }

    return () => {
      if (map && riskMarkersRef.current) {
        riskMarkersRef.current.forEach(marker => marker?.remove());
      }
      riskMarkersRef.current = null;
    }
  }, [map, selectedRisk, selectedHorizon, selectedScenario]);

  if (selectedRegion !== 'Kasungu' && selectedTab !== 'overview') {
    setSelectedTab('overview');
    setSelectedRisk(null);
    setOpenRisk(null);
  }

  return (
    <div className="risk-data-content">
      <div className="risk-data-content__header">
        <h2 className="my-third-risk-step">
          {t('Climate risks in')}
          <Select
            options={REGIONS.map(({ district, lat, lng }) => ({ id: district, label: district, coordinates: [lat, lng] }))}
            value={selectedRegion}
            onInputChange={(e) => {
              setSelectedRegion(e.id);
              trackEvent(`risk_data_region_changed_to_${e.id}`);
              if (map && e.coordinates) {
                map.flyTo({ center: [e.coordinates[1], e.coordinates[0]], zoom: 8 });
              }
            }}
            className="risk-data-content__header__select"
          />
        </h2>
        <div className="risk-data-content__select my-ninth-risk-step">
          <span>{t('Emission scenario')}</span>
          <TranslatedSelect
            options={SCENARIOS.map(({ id, label }) => ({ id, label: t(label) }))}
            menuStyle={{
              maxWidth: 'calc(100vw - 32px)',
              width: 'max-content',
              '& li > button': {
                whiteSpace: 'normal',
              },
            }}
            onInputChange={(e) => {
              onSelectedScenarioChange(e);
              trackEvent(`risk_data_scenario_changed_to_${e.id}`);
            }}
            value={selectedScenario.id}
            className="risk-data-content__select__input"
          />
        </div>
      </div>
      <div className="risk-data-content__tabs my-fourth-risk-step">
        {TABS.map(tab => (
          <button
            className={`risk-data-content__btn risk-data-content__${tab.id} ${selectedTab === tab.id ? ' risk-data-content__btn--active' : ''}`}
            type="button"
            key={tab.id}
            onClick={() => {
              setSelectedTab(tab.id);
              setOpenRisk(tab.defaultRisk || null);
              setSelectedRisk(tab.defaultRisk || null);
              trackEvent(`risk_data_tab_changed_to_${tab.id}`);
            }}
            disabled={selectedRegion !== 'Kasungu' && tab.id !== 'overview'}
          >
            <Icon className="risk-data-content__tab__icon" icon={tab.icon} size="24px" />
            <span className="risk-data-content__tab__text">{t(tab.label)}</span>
          </button>
        ))}
      </div>
      {selectedTab === 'overview' && (
        <RiskDataOverview riskData={riskData} />
      )}
      {selectedTab === 'temperature' && (
        <RiskDataTabLayout
          collapsibleItems={[
            {
              risk: 'fire',
              collapsibleContent: <RiskDataFire selectedScenario={selectedScenario} selectedHorizon={selectedHorizon} />,
            }, {
              risk: 'heat-wave',
              collapsibleContent: <RiskDataHeatWave selectedScenario={selectedScenario} selectedHorizon={selectedHorizon} />,
            },
          ]}
          openRisk={openRisk}
          selectedScenario={selectedScenario}
          selectedHorizon={selectedHorizon}
          setOpenRisk={setOpenRisk}
          isRiskEnabled={isRiskEnabled}
          onRiskEnabledChange={handleRiskEnabledChange}
          setSelectedRisk={setSelectedRisk}
        />
      )}
      {selectedTab === 'water' && (
        <RiskDataTabLayout
          collapsibleItems={[
            {
              risk: 'dry-spell',
              collapsibleContent: <RiskDataDrySpell selectedScenario={selectedScenario} selectedHorizon={selectedHorizon} />,
            },
            {
              risk: 'extreme-precipitation',
              collapsibleContent: (
                <RiskDataExtremePrecipitation
                  map={map}
                  selectedScenario={selectedScenario}
                  selectedHorizon={selectedHorizon}
                  selectedRisk={selectedRisk}
                />
              ),
            },
          ]}
          openRisk={openRisk}
          selectedScenario={selectedScenario}
          selectedHorizon={selectedHorizon}
          setOpenRisk={setOpenRisk}
          isRiskEnabled={isRiskEnabled}
          onRiskEnabledChange={handleRiskEnabledChange}
          setSelectedRisk={setSelectedRisk}
        />
      )}
      {selectedTab === 'wind' && (
        <RiskDataTabLayout
          collapsibleItems={[
            {
              risk: 'extreme-wind',
              collapsibleContent: <RiskDataExtremeWind selectedScenario={selectedScenario} selectedHorizon={selectedHorizon} map={map} />,
            },
          ]}
          openRisk={openRisk}
          selectedScenario={selectedScenario}
          selectedHorizon={selectedHorizon}
          setOpenRisk={setOpenRisk}
          isRiskEnabled={isRiskEnabled}
          onRiskEnabledChange={handleRiskEnabledChange}
          setSelectedRisk={setSelectedRisk}
        />
      )}
    </div>
  )
}

export default RiskDataContent;
